import { Box, Tabs, Text } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-store'
import { getAccess } from 'features/Private/Private'
import isEmpty from 'lodash/isEmpty'
import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { ROLES } from 'shared/constants/user'
import { authStore } from 'shared/store/auth'
import { userCardStore } from 'shared/store/userCard'
import { toaster } from 'uikit/Toaster'
import { CardContainer, Container } from 'uikit/layout'
import { IdentificationsList } from './IdentificationsList/IdentificationsList'
import { IncomingTransfers } from './IncomingTransfers/IncomingTransfers'
import { Payments } from './Payments'
import { Referral } from './Referral'
import { UploadImageModal } from './UploadImageModal/UploadImageModal'
import { User } from './User'
import { UserEdit } from './UserEdit'
import { ImageSnippets } from './blocks/ImageSnippets/ImageSnippets'
import { useFetchUser } from './hooks/useFetchUser'
import { useUpdateEkeng } from './hooks/useUpdateEkeng'

export const UserCard = () => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const { id } = useParams()
  const { paramTabIndex } = useParams()
  const { user } = useStore(authStore)
  const isAdmin = getAccess(user.roles, [ROLES.admin])

  const { data: item, isFetching: loading } = useFetchUser(id)
  const { mutate: updateEkeng, isLoading: ekengIsUpdating } = useUpdateEkeng()

  const [tabIndex, setTabIndex] = useState(Number(paramTabIndex) || 0)
  const isReadyItem = !isEmpty(item)
  
  const handleBack = () => {
    history.goBack()
  }

  useEffect(() => {
    setTabIndex(Number(paramTabIndex))
  }, [paramTabIndex])

  const tabValues = [
    'user-data',
    'payments',
    'incoming-transfers',
    'documents',
    'identifications',
    'referral',
    'ekeng',
  ]

  const handleTabsChange = (details) => {
    const index = tabValues.indexOf(details.value)
    history.replace(`/user/card/${index}/${id}`)
  }

  const onUpdateEkeng = () => {
    updateEkeng(id, {
      onSuccess: (ekengData) => {
        userCardStore.setState((state) => ({
          ...state,
          ekengData,
        }))

        queryClient.invalidateQueries({ queryKey: ['useFetchEkengHistory', 'userCard'] })
        queryClient.invalidateQueries({ queryKey: ['userCard', id] })

        toaster.create({
          type: 'success',
          title: 'Данные успешно обновлены',
        })
      },
    })
  }

  return (
    <Container>
      <CardContainer columns="86px 1fr" indentTop={70} onBack={handleBack}>
        <Tabs.Root
          variant="outline"
          colorPalette="orange"
          onValueChange={handleTabsChange}
          value={tabValues[tabIndex]}
        >
          <Tabs.List>
            <Tabs.Trigger value="user-data">Данные пользователя</Tabs.Trigger>
            <Tabs.Trigger value="payments">Платежи</Tabs.Trigger>
            <Tabs.Trigger value="incoming-transfers">Пополнения</Tabs.Trigger>
            <Tabs.Trigger value="documents">Документы</Tabs.Trigger>
            <Tabs.Trigger value="identifications">Идентификация</Tabs.Trigger>
            <Tabs.Trigger value="referral">Бонусы</Tabs.Trigger>
            <Tabs.Trigger value="ekeng">Смена данных</Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="user-data">
            <User
              userId={id}
              data={item}
              isLoading={loading || ekengIsUpdating}
              ekengIsUpdating={ekengIsUpdating}
              onUpdateEkeng={onUpdateEkeng}
            />
          </Tabs.Content>
          <Tabs.Content value="payments">
            {isReadyItem && <Payments user={item} />}
          </Tabs.Content>
          <Tabs.Content value="incoming-transfers">
            {isReadyItem && <IncomingTransfers />}
          </Tabs.Content>
          <Tabs.Content value="documents">
            <Box mb="30px">
              <UploadImageModal userId={id} />
            </Box>
            {isReadyItem && <ImageSnippets data={item?.identityDocumentScans} />}
          </Tabs.Content>
          <Tabs.Content value="identifications">
            {isReadyItem && <IdentificationsList userId={id} />}
          </Tabs.Content>
          <Tabs.Content value="referral">
            {isAdmin ? (
              isReadyItem && <Referral userId={id} phone={item?.phone} />
            ) : (
              <Text>Недоступно для пользователя</Text>
            )}
          </Tabs.Content>
          <Tabs.Content value="ekeng">
            <UserEdit userId={id} />
          </Tabs.Content>
        </Tabs.Root>
      </CardContainer>
    </Container>
  )
}
